import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import obvmConfig from '../config';
import UserPrefsManager from './user_prefs';

const baseURL = obvmConfig.API.BASE_URL;
const msTimeout = 25000;
const expiredSession = 'Your session has expired. Please log in again.';

const axiosInstance = axios.create({
	baseURL: baseURL,
	timeout: msTimeout,
	headers: {
		Authorization: localStorage.getItem('access_token')
			? 'JWT ' + localStorage.getItem('access_token')
			: null,
		'Content-Type': 'application/json',
	}
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
	(config) => {
		// Get the latest access token from localStorage
		const accessToken = localStorage.getItem('access_token');

		// If the token exists, set it in the Authorization header
		if (accessToken) {
			config.headers['Authorization'] = 'JWT ' + accessToken;
		}

		// Add Idempotency-Key header for POST, PUT, DELETE requests
		if (['post', 'put', 'delete'].includes(config.method)) {
			if (!config.headers['Idempotency-Key']) {
				config.headers['Idempotency-Key'] = uuidv4();
			}
		}

		return config;
	},
	(error) => {
		// Handle request errors
		return Promise.reject(error);
	}
);

// Token refresh queue handling
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh
function subscribeTokenRefresh(cb) {
	refreshSubscribers.push(cb);
}

function onRefreshed(token) {
	refreshSubscribers.forEach(cb => cb(token));
	refreshSubscribers = [];
}

// Axios response interceptor
axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		if (!error.response) {
			alert('A server/network error occurred. Please check your connection and try again.');
			return Promise.reject(error);
		}

		if (error.response && error.response.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			// Wait for the refresh to finish, then retry
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					subscribeTokenRefresh((token) => {
						if (token) {
							originalRequest.headers['Authorization'] = 'JWT ' + token;
							resolve(axiosInstance(originalRequest));
						} else {
							handleLogoutAndRedirect();  // If the refresh fails, log the user out
							reject(error);
						}
					});
				});
			}

			isRefreshing = true;			
			return new Promise(async (resolve, reject) => {
				try {
					const newAccessToken = await RefreshJWT();

					onRefreshed(newAccessToken);
					originalRequest.headers['Authorization'] = 'JWT ' + newAccessToken;
					resolve(axiosInstance(originalRequest));
				} catch (refreshError) {
					console.error('Token refresh failed:', refreshError);
					onRefreshed(null);
					handleLogoutAndRedirect();  // If the refresh fails, log the user out
					reject(refreshError);
				} finally {
					isRefreshing = false;
				}
			});
		}

		return Promise.reject(error);
	}
);

export default axiosInstance;

// Handle logout and redirect
function handleLogoutAndRedirect(message = expiredSession) {
	const currentUrl = window.location.pathname + window.location.search + window.location.hash;
	const loginUrl = '/login?next=' + encodeURIComponent(currentUrl);

	Logout();
	alert(message);

	window.location.href = loginUrl;
}

// Token management utility object
export const TokenManager = {
	save(access, refresh = '') {
		if (access) {
			localStorage.setItem('access_token', access);
			axiosInstance.defaults.headers['Authorization'] = 'JWT ' + access;
		}

		if (refresh) {
			localStorage.setItem('refresh_token', refresh);
		}
	},

	remove() {
		localStorage.removeItem('access_token');
		localStorage.removeItem('refresh_token');
		axiosInstance.defaults.headers['Authorization'] = null;
	},

	getAccess() {
		return localStorage.getItem('access_token');
	},

	getRefresh() {
		return localStorage.getItem('refresh_token');
	},

	isValid(token) {
		if (typeof token === 'string') {
			try {
				const jwtParts = JSON.parse(window.atob(token.split('.')[1]));
				const now = Math.ceil(Date.now() / 1000);
				return jwtParts.exp > now;
			} catch (error) {
				console.error('Invalid JWT token:', error);
				return false;
			}
		}
		console.error('Invalid JWT token format');
		return false;
	},

	isAccessValid() {
		const token = this.getAccess();
		return token ? this.isValid(token) : false;
	},

	isRefreshValid() {
		const token = this.getRefresh();
		return token ? this.isValid(token) : false;
	}
};

// Token refresh function
export async function RefreshJWT() {
	const refreshToken = TokenManager.getRefresh();

	if (!refreshToken) {
		throw new Error('Refresh token not available');
	}

	if (!TokenManager.isValid(refreshToken)) {
		throw new Error('Refresh token is expired');
	}

	try {
		const response = await axiosInstance.post('auth/refresh/', { refresh: refreshToken });
		TokenManager.save(response.data.access, response.data.refresh);

		return response.data.access;
	} catch (error) {
		console.error('Failed to refresh access token:', error);
		throw error;
	}
}

// Validate the Access token, or attempt to refresh it
export async function ValidateTokens() {
	if (TokenManager.isAccessValid()) return true;

	if (TokenManager.isRefreshValid()) {
		try {
			const newAccessToken = await RefreshJWT();
			return TokenManager.isValid(newAccessToken);
		} catch (error) {
			console.error('Failed to refresh token:', error);
		}
	}
	return false;
}

// Log the user out and delete tokens
export function Logout() {
	const refresh = TokenManager.getRefresh();
	if (refresh) {
		axiosInstance.post('auth/blacklist/', { refresh })
			.catch((error) => {});
	}
	UserPrefsManager.removeAll();
	TokenManager.remove();
}